import type { GeneratedBackground } from "./BackgroundGenerator.js";
import { randomInt } from "@engine/combat/dice.js";

/**
 * Offline fallback for `LLMClient`-backed character creation (L05).
 *
 * `specs/llm-integrations.md §Offline fallback` commits to shipping a
 * pre-authored background pool so the player can still create a party
 * with zero API keys, a dead network, or a quota failure mid-session.
 * Portraits are no longer LLM-generated — the static pool under
 * `public/engine/portraits/pool/` replaces that (see `portraitPool.ts`).
 */

type Rng = (min: number, max: number) => number;

// ── Background bank ──────────────────────────────────────────────────────────

/**
 * Hand-authored background stubs. Each covers a common class archetype,
 * but the bank is small on purpose — five seeds are enough to avoid
 * meta-déjà-vu on a 4-member party.
 */
export const FALLBACK_BACKGROUNDS: readonly GeneratedBackground[] = [
  {
    backgroundId: "soldier",
    name: "The Last Sergeant",
    trait: "I keep a steady voice when things go wrong.",
    ideal: "The squad always comes home.",
    bond: "The name etched on my pauldron.",
    flaw: "I follow orders I shouldn't.",
    backstory:
      "A veteran of a campaign nobody remembers. Woke up alive on the field when the rest of the company didn't. Took the road south looking for honest work and bad company.",
    skillProficiencies: ["Athletics", "Intimidation"],
    startingEquipment: ["shortsword", "leather_armor"],
    // Fighters, Barbarians, Paladins
  },
  {
    backgroundId: "sage",
    name: "Restless Scholar",
    trait: "I read the room the way others read books.",
    ideal: "Knowledge is worth the price.",
    bond: "A library that turned me out.",
    flaw: "I hoard every scrap of lore like it's gold.",
    backstory:
      "Apprenticed to a dusty old archivist until the wrong question got them expelled. Now they wander — notebook in hand, ink on their fingers, trouble at their heels.",
    skillProficiencies: ["Arcana", "History"],
    startingEquipment: ["dagger", "scholars_pack"],
    // Wizards, Clerics, Bards
  },
  {
    backgroundId: "outlander",
    name: "Child of the Long Road",
    trait: "Cities make me itch; the forest doesn't.",
    ideal: "Nothing is ever truly lost — you just haven't tracked it yet.",
    bond: "A trail my mother walked before me.",
    flaw: "I trust animals before I trust people.",
    backstory:
      "Grew up moving camp every season, learning names for every wind and weather. Came into a town for the first time six years ago and still hasn't quite forgiven it.",
    skillProficiencies: ["Survival", "Perception"],
    startingEquipment: ["shortbow", "hunters_trap"],
    // Rangers, Druids
  },
  {
    backgroundId: "criminal",
    name: "Lockpick's Heir",
    trait: "I say the wrong thing on purpose, just to watch.",
    ideal: "Quick hands, quick feet, quicker wits.",
    bond: "One partner still owes me — and I owe them.",
    flaw: "I can't let a locked door stay locked.",
    backstory:
      "Cut their teeth lifting purses and learned to read faces better than ledgers. The last job went sideways; the only souvenir is a scar and a reputation.",
    skillProficiencies: ["Stealth", "Deception"],
    startingEquipment: ["dagger", "thieves-tools"],
    // Rogues, Bards
  },
  {
    backgroundId: "acolyte",
    name: "Doubter of the Cloister",
    trait: "I listen more than I speak.",
    ideal: "Mercy, even when it costs.",
    bond: "The temple that raised me.",
    flaw: "I expect the best of people who don't deserve it.",
    backstory:
      "Spent a decade in cold stone halls and prayer candles, then one morning walked out without a word. Still keeps the holy symbol; still isn't sure why.",
    skillProficiencies: ["Religion", "Insight"],
    startingEquipment: ["mace", "holy_symbol"],
    // Clerics, Paladins
  },
];

/** Class → background-index hints. Entries not listed fall back to uniform pick. */
const CLASS_HINTS: Readonly<Record<string, number>> = {
  fighter: 0,
  barbarian: 0,
  paladin: 4,
  wizard: 1,
  cleric: 4,
  bard: 3,
  ranger: 2,
  druid: 2,
  rogue: 3,
  warlock: 1,
  sorcerer: 1,
  monk: 0,
};

export interface FallbackRequest {
  readonly race: string;
  readonly characterClass: string;
}

/**
 * Pick a fallback background. Class-appropriate when we have an entry,
 * otherwise uniform over the whole bank using the seeded engine RNG.
 */
export function pickFallbackBackground(
  request: FallbackRequest,
  rng: Rng = randomInt,
): GeneratedBackground {
  const classKey = request.characterClass.trim().toLowerCase();
  const hinted = CLASS_HINTS[classKey];
  if (hinted !== undefined) {
    const entry = FALLBACK_BACKGROUNDS[hinted];
    if (entry) return entry;
  }
  const idx = rng(0, FALLBACK_BACKGROUNDS.length - 1);
  return FALLBACK_BACKGROUNDS[idx]!;
}

